import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { Alert, Anchor, Button, Paper, Stack, Text, Title } from '@mantine/core'
import type { InviteDoc } from '@racedash/shared'
import { useShell } from '../AppShell'
import { acceptInvite, readInvite } from '../org/members'

type State =
  | { status: 'loading' }
  | { status: 'ready'; invite: InviteDoc }
  | { status: 'problem'; message: string }

/**
 * Accepting an invite. Sits inside the authenticated shell on purpose: joining
 * needs an identity to attach the membership to, so an unauthenticated visitor
 * is shown the sign-in panel first and lands back here afterwards.
 */
export default function Join() {
  const { token } = useParams()
  const { user, orgs } = useShell()
  const navigate = useNavigate()
  const [state, setState] = useState<State>({ status: 'loading' })
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!token) return
    void readInvite(token).then((invite) => {
      if (!invite) {
        setState({
          status: 'problem',
          message: 'This invite link is not valid. It may have been revoked.',
        })
      } else if (invite.acceptedBy) {
        setState({ status: 'problem', message: 'This invite has already been used.' })
      } else if (invite.expiresAt.toMillis() <= Date.now()) {
        setState({ status: 'problem', message: 'This invite has expired. Ask for a new one.' })
      } else {
        setState({ status: 'ready', invite })
      }
    })
  }, [token])

  if (state.status === 'loading') return <Text c="dimmed">Loading…</Text>

  if (state.status === 'problem') {
    return (
      <Paper withBorder p="lg" maw={560}>
        <Stack gap="xs">
          <Title order={1} size="h3">
            Can't use this invite
          </Title>
          <Text c="dimmed" size="sm">
            {state.message}{' '}
            <Anchor component={Link} to="/">
              Go to your dashboards
            </Anchor>
          </Text>
        </Stack>
      </Paper>
    )
  }

  const { invite } = state
  const alreadyIn = orgs.some((o) => o.id === invite.orgId)

  async function accept() {
    if (!token || busy) return
    setBusy(true)
    const result = await acceptInvite(token, user)
    setBusy(false)
    if (result.ok) navigate(`/o/${result.orgId}`, { replace: true })
    else setState({ status: 'problem', message: `Couldn't join (${result.reason}).` })
  }

  return (
    <Paper withBorder p="lg" maw={560}>
      <Stack gap="xs">
        <Text size="xs" c="dimmed" tt="uppercase" fw={600} style={{ letterSpacing: '0.08em' }}>
          Invitation
        </Text>
        <Title order={1} size="h3">
          Join {invite.orgName}
        </Title>
        <Text c="dimmed" size="sm">
          You've been invited as {invite.role === 'owner' ? 'an' : 'a'}{' '}
          <strong>{invite.role}</strong>.{' '}
          {invite.role === 'viewer'
            ? 'You will be able to see the organisation dashboards.'
            : invite.role === 'editor'
              ? 'You will be able to create and edit dashboards, and share them.'
              : 'You will be able to manage dashboards, people, and the organisation itself.'}
        </Text>
        <Text c="dimmed" size="xs">
          The invite was sent to {invite.email}. Signing in with a different address is fine — the
          link is what admits you.
        </Text>

        {alreadyIn ? (
          <Alert color="gray" variant="light" mt="md">
            You're already a member of this organisation.{' '}
            <Anchor component={Link} to={`/o/${invite.orgId}`}>
              Open it
            </Anchor>
          </Alert>
        ) : (
          <Button mt="md" loading={busy} onClick={() => void accept()}>
            Accept invitation
          </Button>
        )}
      </Stack>
    </Paper>
  )
}
